import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import { ExportFileType, SearchParam } from '@common/ts/enums';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import {
  useMutateDownloadConversationDetails,
  useMutateDownloadConversationOverview,
  useMutateDownloadConversation,
  useMutateDownloadFullAnswersTranscript,
} from '@/reactQuery/get';
import { ExportModalProps, ExportOption, MultipleConversationsView } from '@/types';
import { ConversationView } from '@/enums';
import exportConversation from '@/utils/exportConversation';
import ExportFileTypeToggle from '@/components/Modals/ExportModal/ExportFileTypeToggle';
import ExportOptionsDropdown from '@/components/Modals/ExportModal/ExportOptionsDropdown';

export default function ExportModal({
  isOpen,
  onClose,
  projectId,
  transcriptsView,
}: ExportModalProps) {
  const { lang } = useTranslation();
  // Ordered by what researchers reach for most: the one-column-per-question
  // shape (best for analysis) comes first and is the default selection, so the
  // wide "answers in columns" export is no longer mistaken for the stacked
  // per-message "full conversation" export. Labels describe the row grain
  // (one row per respondent vs per message), not the internal data shape.
  const exportOptions: ExportOption[] = [
    {
      label: lang.get('msg.exportFullAnswersTranscript'),
      description: lang.get('msg.exportFullAnswersTranscriptDesc'),
      shape: 'wide',
      value: ConversationView.FULL_ANSWERS_TRANSCRIPT,
    },
    {
      label: lang.get('msg.exportConversationDetails'),
      description: lang.get('msg.exportConversationDetailsDesc'),
      shape: 'tall',
      value: ConversationView.CONVERSATION_DETAILS,
    },
    {
      label: lang.get('msg.exportConversationOverview'),
      description: lang.get('msg.exportConversationOverviewDesc'),
      shape: 'wide',
      value: ConversationView.CONVERSATION_OVERVIEW,
    },
  ];
  const [selectedOption, setSelectedOption] = useState<ExportOption | null>(() => exportOptions[0]);
  // XLSX is the default: it opens cleanly in Excel across every OS locale
  // (no `sep=,` hack, no UTF-8-BOM mojibake), which was the #1 export pain.
  // CSV/TXT remain selectable for pipelines (pandas/R/BI).
  const [fileType, setFileType] = useState<ExportFileType>(ExportFileType.XLSX);
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const conversationId = searchParams.get(SearchParam.SELECTED_TRANSCRIPT);
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE) || undefined;
  const isSingleConversationScreen = !!conversationId;

  const {
    mutateAsync: mutateAsyncDownloadConversationDetails,
    isPending: isDownloadingConversationDetailsPending,
  } = useMutateDownloadConversationDetails();

  const {
    mutateAsync: mutateAsyncDownloadConversationOverview,
    isPending: isDownloadingConversationOverviewPending,
  } = useMutateDownloadConversationOverview();

  const {
    mutateAsync: mutateAsyncDownloadConversation,
    isPending: isDownloadingConversationPending,
  } = useMutateDownloadConversation(projectId);

  const {
    mutateAsync: mutateAsyncDownloadFullAnswersTranscript,
    isPending: isDownloadingFullAnswersTranscriptPending,
  } = useMutateDownloadFullAnswersTranscript();

  // Helper function to execute export with the right parameters
  const executeExport = async (type: ConversationView) => {
    await exportConversation({
      type,
      projectId,
      conversationId,
      filterId,
      fileType,
      transcriptsView,
      language: isSingleConversationScreen ? translationLanguage : undefined,
      mutations: {
        overview: mutateAsyncDownloadConversationOverview,
        details: mutateAsyncDownloadConversationDetails,
        single: mutateAsyncDownloadConversation,
        fullAnswersTranscript: mutateAsyncDownloadFullAnswersTranscript,
      },
      translations: {
        overview: lang.get('msg.conversationOverviewSuccessfullyDownloaded'),
        details: lang.get('msg.allConversationDetailsSuccessfullyDownloaded'),
        single: lang.get('msg.conversationSuccessfullyDownloaded'),
        fullAnswersTranscript: lang.get('msg.fullAnswersTranscriptSuccessfullyDownloaded'),
        error: lang.get('msg.errorPleaseTryAgain'),
      },
    });
  };

  const exportConversationOverview = () => executeExport(ConversationView.CONVERSATION_OVERVIEW);
  const exportConversationDetails = () => executeExport(ConversationView.CONVERSATION_DETAILS);
  const exportSingleConversation = () => executeExport(ConversationView.SINGLE_CONVERSATION);
  const exportFullAnswersTranscript = () => executeExport(ConversationView.FULL_ANSWERS_TRANSCRIPT);

  const onCloseHandler = () => {
    onClose();
    setSelectedOption(exportOptions[0]);
    setFileType(ExportFileType.XLSX);
  };

  // Handle export button click
  const handleExport = async () => {
    try {
      // If we're on the single transcript page, use that export function directly
      if (isSingleConversationScreen) {
        await exportSingleConversation();
      } else {
        // Otherwise, use the selected option for regular exports
        if (!selectedOption) return;

        const actions: Record<MultipleConversationsView, () => Promise<void>> = {
          [ConversationView.CONVERSATION_OVERVIEW]: exportConversationOverview,
          [ConversationView.CONVERSATION_DETAILS]: exportConversationDetails,
          [ConversationView.FULL_ANSWERS_TRANSCRIPT]: exportFullAnswersTranscript,
        };

        await actions[selectedOption.value as MultipleConversationsView]();
      }

      onCloseHandler();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const isExporting =
    isDownloadingConversationDetailsPending ||
    isDownloadingConversationOverviewPending ||
    isDownloadingConversationPending ||
    isDownloadingFullAnswersTranscriptPending;

  const isExportDisabled = isExporting || (!isSingleConversationScreen && selectedOption === null);

  return (
    <Dialog open={isOpen} onOpenChange={onCloseHandler}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{lang.get('msg.exportData')}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 items-center">
          {!isSingleConversationScreen ? (
            <>
              <ExportOptionsDropdown
                exportOptions={exportOptions}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
              />

              <span className="text-sm mx-1">{lang.get('msg.as')}</span>
            </>
          ) : (
            <p className="text-sm">{lang.get('msg.exportConversation')}</p>
          )}

          <ExportFileTypeToggle fileType={fileType} setFileType={setFileType} />
        </div>

        <DialogFooter className="flex justify-end mt-11">
          <Button variant="outline" onClick={onCloseHandler} disabled={isExporting}>
            {lang.get('msg.cancel')}
          </Button>
          <Button onClick={handleExport} disabled={isExportDisabled}>
            {isExporting && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />}
            {lang.get(isExporting ? 'msg.exporting' : 'msg.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
